import api from './axios';
import type {
  CustomerCheckResponse,
  CustomerRegistration,
  Customer,
  Coupon,
  PointHistoryItem,
  PointsExpiring,
  StampStatus
} from "../types";

// ============================================================================
// MOCK API CONFIGURATION
// ============================================================================
// Set this to true to use mocked API responses for testing
// Set this to false to use real API endpoints
const USE_MOCK_API = false; // TODO: Change to false when server is ready
// ============================================================================

// Log mock status on module load
if (USE_MOCK_API) {
  console.log('🚀 Mock API is ENABLED - All API calls will return mocked responses');
} else {
  console.log('🌐 Real API is ENABLED - API calls will go to actual endpoints');
}

/**
 * Check if a customer exists in the database
 * @param userId - Firebase user ID
 * @returns Promise<CustomerCheckResponse>
 */
export const checkCustomerExists = async (): Promise<CustomerCheckResponse> => {
  try {
    const response = await api.get<Customer>(`/api/members/me`);
    return {
      exists: true,
      customer: response.data
    };
  } catch (error) {
    console.error('Error checking customer exists:', error);
    return {
      exists: false,
      customer: undefined
    };
  }
};

/**
 * Register a new customer
 * @param customerData - Customer registration data
 * @returns Promise<RegistrationResponse>
 */
export const registerCustomer = async (customerData: CustomerRegistration): Promise<Customer> => {
  try {
    const response = await api.post<Customer>('/api/members/register', customerData);
    return response.data;
  } catch (error) {
    console.error('Error registering customer:', error);
    throw error;
  }
};

/**
 * Convert registration form data to API format
 * @param firebaseUser - Firebase user object
 * @param formData - Registration form data
 * @returns CustomerRegistrationData
 */
export const convertRegistrationData = (
  firebaseUser: any,
  formData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: {
      year: string;
      month: string;
      day: string;
    };
    gender?: string;
    referralCode?: string;
  }
): CustomerRegistration => {
  let dob = '';
  if (formData.dateOfBirth?.year && formData.dateOfBirth?.month && formData.dateOfBirth?.day) {
    dob = new Date(
      parseInt(formData.dateOfBirth.year),
      parseInt(formData.dateOfBirth.month) - 1,
      parseInt(formData.dateOfBirth.day)
    ).toISOString().split('T')[0]; // Format as 'YYYY-MM-DD'
  }

  return {
    firstName: formData.firstName?.trim() || 'Guest',
    lastName: formData.lastName?.trim() || 'User',
    phone: formData.phone || firebaseUser.phoneNumber || "",
    dob: dob,
    gender: formData.gender || null,
    referrer: formData.referralCode || null,
  };
};

/**
 * Fetch member coupons (active - no pagination support)
 * @returns Promise<Coupon[]>
 */
export async function fetchMemberCoupons(): Promise<Coupon[]> {
  try {
    const response = await api.get<Coupon[]>('/api/member/coupons');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch member coupons:', error);
    throw error;
  }
}

/**
 * Fetch expired member coupons
 * @param page - Page number (0-indexed)
 * @param size - Number of items per page
 * @returns Promise<{ content: Coupon[], totalPages: number, totalElements: number }>
 */
export async function fetchMemberCouponsExpired(page = 0, size = 20): Promise<{ content: Coupon[], totalPages: number, totalElements: number }> {
  try {
    const response = await api.get<{ content: Coupon[], totalPages: number, totalElements: number }>('/api/member/coupons/expired', {
      params: {
        page,
        size,
        sortBy: 'expiresAt',
        sortDirection: 'DESC'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch expired member coupons:', error);
    throw error;
  }
}

/**
 * Fetch used member coupons
 * @param page - Page number (0-indexed)
 * @param size - Number of items per page
 * @returns Promise<{ content: Coupon[], totalPages: number, totalElements: number }>
 */
export async function fetchMemberCouponsUsed(page = 0, size = 20): Promise<{ content: Coupon[], totalPages: number, totalElements: number }> {
  try {
    const response = await api.get<{ content: Coupon[], totalPages: number, totalElements: number }>('/api/member/coupons/used', {
      params: {
        page,
        size,
        sortBy: 'redeemedAt',
        sortDirection: 'DESC'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch used member coupons:', error);
    throw error;
  }
}

/**
 * Fetch point history
 * @param page - Page number (0-indexed)
 * @param size - Number of items per page
 * @returns Promise<{ content: PointHistoryItem[], totalPages: number, totalElements: number }>
 */
export async function fetchPointHistory(page = 0, size = 20): Promise<{ content: PointHistoryItem[], totalPages: number, totalElements: number }> {
  try {
    const response = await api.get<{ content: PointHistoryItem[], totalPages: number, totalElements: number }>('/api/member/points/history', {
      params: { page, size }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch point history:', error);
    throw error;
  }
}

/**
 * Fetch expiring points
 * @returns Promise<PointsExpiring>
 */
export async function fetchPointsExpiring(): Promise<PointsExpiring> {
  try {
    const response = await api.get<PointsExpiring>('/api/member/points/expiring');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch expiring points:', error);
    throw error;
  }
}

/**
 * Fetch stamp card status for the current member
 * @returns Promise<StampStatus>
 */
export async function fetchStampStatus(): Promise<StampStatus> {
  const response = await api.get<StampStatus>('/api/member/stamps');
  return response.data;
}

export async function fetchFeaturedInstagramPosts(): Promise<import('./types').FeaturedInstagramPost[]> {
  const response = await api.get<import('./types').FeaturedInstagramPost[]>('/api/public/instagram/featured');
  return response.data;
}

export async function fetchAllInstagramPosts(page = 1, limit = 24): Promise<import('./types').InstagramPostsPage> {
  const response = await api.get<import('./types').InstagramPostsPage>('/api/public/instagram/posts', { params: { page, limit } });
  return response.data;
}

export async function fetchSpendingHistory(page = 0, size = 20): Promise<{ content: import('./types').SpendingHistoryEntry[], totalPages: number, totalElements: number }> {
  const response = await api.get<{ content: import('./types').SpendingHistoryEntry[], totalPages: number, totalElements: number }>('/api/member/spending/history', { params: { page, size } });
  return response.data;
}

export async function fetchReceiptHistory(page = 0, size = 20): Promise<{ content: import('./types').ReceiptSubmissionItem[], totalPages: number, totalElements: number }> {
  const response = await api.get<{ content: import('./types').ReceiptSubmissionItem[], totalPages: number, totalElements: number }>('/api/member/receipt-submissions', { params: { page, size } });
  return response.data;
}

export async function fetchOrderHistory(page = 0, size = 20): Promise<{ content: import('./types').OrderItem[], totalPages: number, totalElements: number }> {
  const response = await api.get<{ content: import('./types').OrderItem[], totalPages: number, totalElements: number }>('/api/member/orders', { params: { page, size } });
  return response.data;
}

export async function fetchAvailablePointRedemptions(): Promise<import('../types').Coupon[]> {
  const response = await api.get<import('../types').Coupon[]>('/api/member/point-redemptions/available');
  return response.data;
}

export async function redeemCouponByPoints(couponId: string): Promise<import('../types').PointRedemptionResponse> {
  const response = await api.post<import('../types').PointRedemptionResponse>(`/api/member/point-redemptions/${couponId}`);
  return response.data;
}